// 管理员相关类型定义

// 登录凭据
export interface LoginCredentials {
  username: string
  password: string
  remember?: boolean
}

// 登录响应
export interface LoginResponse {
  access_token: string
  token_type: string
  expires_in: number
  user: AdminUser
}

// 管理员用户信息
export interface AdminUser {
  id: number
  username: string
  email: string
  display_name?: string
  avatar?: string
  role: string
  permissions: string[]
  is_active: boolean
  last_login_at?: string
  login_attempts: number
  locked_until?: string
  created_at: string
  updated_at?: string
  created_by?: number
}

// 管理员角色
export enum AdminRole {
  SUPER_ADMIN = 'super_admin',      // 超级管理员
  CONTENT_ADMIN = 'content_admin',  // 内容管理员
  DATA_ANALYST = 'data_analyst',    // 数据分析师
  OPERATOR = 'operator'             // 运营人员
}

// 管理员登录请求
export interface AdminLoginRequest {
  username: string
  password: string
  remember?: boolean
  captcha?: string
}

// 管理员登录响应
export interface AdminLoginResponse {
  user: AdminUser
  token: string
  expires_in: number
  refresh_token?: string
}

// 管理员注册请求
export interface AdminRegisterRequest {
  username: string
  email: string
  password: string
  confirm_password: string
  display_name?: string
  role: AdminRole
  permissions?: string[]
}

// 管理员更新请求
export interface AdminUpdateRequest {
  display_name?: string
  email?: string
  avatar?: string
  is_active?: boolean
  role?: AdminRole
  permissions?: string[]
}

// 管理员密码修改请求
export interface AdminPasswordChangeRequest {
  old_password: string
  new_password: string
  confirm_password: string
}

// 管理员列表查询参数
export interface AdminListParams {
  page?: number
  page_size?: number
  search?: string
  role?: AdminRole
  is_active?: boolean
  sort_by?: 'created_at' | 'last_login' | 'username'
  sort_order?: 'asc' | 'desc'
}

// 管理员统计信息
export interface AdminStats {
  total_admins: number
  active_admins: number
  online_admins: number
  role_distribution: Record<AdminRole, number>
  recent_logins: Array<{
    admin_id: number
    username: string
    login_time: string
    ip_address: string
  }>
}

// 管理员活动日志
export interface AdminActivityLog {
  log_id: number
  admin_id: number
  admin_username: string
  action: string
  resource_type: string
  resource_id?: number
  details?: Record<string, any>
  ip_address: string
  user_agent: string
  timestamp: string
}

// 管理员权限
export enum AdminPermission {
  // 用户管理权限
  USER_VIEW = 'user:view',
  USER_EDIT = 'user:edit',
  USER_DELETE = 'user:delete',
  USER_CREATE = 'user:create',
  
  // 内容管理权限
  CONTENT_VIEW = 'content:view',
  CONTENT_AUDIT = 'content:audit',
  CONTENT_EDIT = 'content:edit',
  CONTENT_DELETE = 'content:delete',
  
  // 词汇管理权限
  VOCABULARY_VIEW = 'vocabulary:view',
  VOCABULARY_EDIT = 'vocabulary:edit',
  VOCABULARY_DELETE = 'vocabulary:delete',
  
  // 数据分析权限
  ANALYTICS_VIEW = 'analytics:view',
  ANALYTICS_EXPORT = 'analytics:export',
  
  // 系统管理权限
  SYSTEM_CONFIG = 'system:config',
  SYSTEM_MAINTAIN = 'system:maintain',
  AI_MODEL_CONFIG = 'ai_model:config',
  
  // 运营管理权限
  OPERATION_VIEW = 'operation:view',
  OPERATION_MANAGE = 'operation:manage',
  
  // 管理员管理权限
  ADMIN_VIEW = 'admin:view',
  ADMIN_EDIT = 'admin:edit',
  ADMIN_DELETE = 'admin:delete',
  ADMIN_CREATE = 'admin:create'
}

// 管理员会话信息
export interface AdminSession {
  session_id: string
  admin_id: number
  ip_address: string
  user_agent: string
  created_at: string
  last_activity: string
  expires_at: string
  is_active: boolean
}

// 管理员错误类型
export interface AdminError {
  code: string
  message: string
  details?: any
  timestamp?: string
}

// 管理员API响应类型
export interface AdminApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  error?: AdminError
  pagination?: {
    total: number
    current: number
    page_size: number
  }
}

// 角色权限映射
export const ROLE_PERMISSIONS: Record<AdminRole, AdminPermission[]> = {
  [AdminRole.SUPER_ADMIN]: Object.values(AdminPermission), // 所有权限
  
  [AdminRole.CONTENT_ADMIN]: [
    AdminPermission.USER_VIEW,
    AdminPermission.CONTENT_VIEW,
    AdminPermission.CONTENT_AUDIT,
    AdminPermission.CONTENT_EDIT,
    AdminPermission.CONTENT_DELETE,
    AdminPermission.VOCABULARY_VIEW,
    AdminPermission.VOCABULARY_EDIT,
    AdminPermission.VOCABULARY_DELETE,
    AdminPermission.ANALYTICS_VIEW
  ],
  
  [AdminRole.DATA_ANALYST]: [
    AdminPermission.USER_VIEW,
    AdminPermission.CONTENT_VIEW,
    AdminPermission.VOCABULARY_VIEW,
    AdminPermission.ANALYTICS_VIEW,
    AdminPermission.ANALYTICS_EXPORT,
    AdminPermission.OPERATION_VIEW
  ],
  
  [AdminRole.OPERATOR]: [
    AdminPermission.USER_VIEW,
    AdminPermission.CONTENT_VIEW,
    AdminPermission.VOCABULARY_VIEW,
    AdminPermission.ANALYTICS_VIEW,
    AdminPermission.OPERATION_VIEW,
    AdminPermission.OPERATION_MANAGE
  ]
}

// 角色显示名称
export const ROLE_NAMES: Record<AdminRole, string> = {
  [AdminRole.SUPER_ADMIN]: '超级管理员',
  [AdminRole.CONTENT_ADMIN]: '内容管理员',
  [AdminRole.DATA_ANALYST]: '数据分析师',
  [AdminRole.OPERATOR]: '运营人员'
}

// 权限显示名称
export const PERMISSION_NAMES: Record<AdminPermission, string> = {
  [AdminPermission.USER_VIEW]: '查看用户',
  [AdminPermission.USER_EDIT]: '编辑用户',
  [AdminPermission.USER_DELETE]: '删除用户',
  [AdminPermission.USER_CREATE]: '创建用户',
  
  [AdminPermission.CONTENT_VIEW]: '查看内容',
  [AdminPermission.CONTENT_AUDIT]: '审核内容',
  [AdminPermission.CONTENT_EDIT]: '编辑内容',
  [AdminPermission.CONTENT_DELETE]: '删除内容',
  
  [AdminPermission.VOCABULARY_VIEW]: '查看词汇',
  [AdminPermission.VOCABULARY_EDIT]: '编辑词汇',
  [AdminPermission.VOCABULARY_DELETE]: '删除词汇',
  
  [AdminPermission.ANALYTICS_VIEW]: '查看分析',
  [AdminPermission.ANALYTICS_EXPORT]: '导出数据',
  
  [AdminPermission.SYSTEM_CONFIG]: '系统配置',
  [AdminPermission.SYSTEM_MAINTAIN]: '系统维护',
  [AdminPermission.AI_MODEL_CONFIG]: 'AI模型配置',
  
  [AdminPermission.OPERATION_VIEW]: '查看运营',
  [AdminPermission.OPERATION_MANAGE]: '运营管理',
  
  [AdminPermission.ADMIN_VIEW]: '查看管理员',
  [AdminPermission.ADMIN_EDIT]: '编辑管理员',
  [AdminPermission.ADMIN_DELETE]: '删除管理员',
  [AdminPermission.ADMIN_CREATE]: '创建管理员'
}

// ========== 管理员管理功能特有类型 ==========

// 管理员列表项（用于列表展示）
export interface AdminListItem {
  id: number
  username: string
  email: string
  display_name?: string
  avatar?: string
  role: AdminRole
  is_active: boolean
  last_login_at?: string
  created_at: string
}

// 管理员详情（用于详情查看）
export interface AdminDetailInfo extends AdminListItem {
  permissions: string[]
  login_attempts: number
  locked_until?: string
  created_by?: number
  updated_at?: string
}

// 管理员创建表单数据
export interface AdminCreateForm {
  username: string
  email: string
  password: string
  display_name?: string
  role: AdminRole
  permissions?: string[]
}

// 管理员基本信息更新表单数据
export interface AdminBasicUpdateForm {
  email?: string
  display_name?: string
  avatar?: string
}

// 角色更新数据
export interface RoleUpdateData {
  role: AdminRole
}

// 权限更新数据
export interface PermissionsUpdateData {
  permissions: string[]
  mode: 'replace' | 'add' | 'remove'
}

// 状态更新数据
export interface StatusUpdateData {
  is_active: boolean
  reason?: string
}

// 密码重置数据
export interface PasswordResetData {
  new_password: string
}

// 个人信息更新数据
export interface ProfileUpdateData {
  email?: string
  display_name?: string
  avatar?: string
  current_password?: string
  new_password?: string
}

// 管理员管理列表响应
export interface AdminManagementListResponse {
  data: AdminListItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// 管理员统计数据
export interface AdminManagementStats {
  total_admins: number
  active_admins: number
  inactive_admins: number
  role_distribution: {
    super_admin: number
    content_admin: number
    data_analyst: number
    operator: number
  }
}
